import * as React from 'react';
import { motion } from 'framer-motion';
import BackgroundBubbles from '@/components/common/BackgroundBubbles';
import blurBg from '@/assets/images/elements/blur_background.webp';
import authElement from '@/assets/images/elements/auth_element.svg';
import logoVarTwo from '@/assets/images/svg/logo_var_two.svg';
import rightArrow from '@/assets/images/svg/right_arrow.svg';

// S.O.C Components
import { AuthCardFrame } from './auth/AuthCardFrame';
import { AuthCarousel } from './auth/AuthCarousel';
import { AuthFormPanel } from './auth/AuthFormPanel';
import { ForgotPassword } from '@/components/auth/ForgotPassword';

export type AuthMode = 'login' | 'register' | 'forgot' | 'verify-pending' | 'verify-complete';

export interface AuthLayoutProps {
  children: React.ReactNode;
  authType: AuthMode;
  onSwitch: () => void;
  isLoading?: boolean;
  submitLabel: string;
  dividerText: string;
  formId: string;
  utmEcho?: string | null;
  isAuthenticated?: boolean;
  onChatNow?: () => void;
}

/**
 * Authentication layout component with animated background and header.
 * Displays login, register, or forgot password forms within a centered card.
 * Includes logo and "More about decho" link positioned above the auth card.
 *
 * @param {AuthLayoutProps} props - Component props
 * @param {React.ReactNode} props.children - Form content (LoginForm or RegisterForm)
 * @param {AuthMode} props.authType - Current authentication mode
 * @param {() => void} props.onSwitch - Callback to switch between login/register modes
 * @param {boolean} [props.isLoading=false] - Loading state for form submission
 * @param {string} props.submitLabel - Text for submit button
 * @param {string} props.dividerText - Text for OAuth divider
 * @param {string} props.formId - HTML form ID for external submit button
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  authType,
  onSwitch,
  isLoading = false,
  submitLabel,
  dividerText,
  formId,
  utmEcho,
  isAuthenticated = false,
  onChatNow,
}) => {
  const hasUtm = !!utmEcho;

  return (
    <div className='relative flex h-dvh w-full items-center justify-center overflow-hidden bg-brand-800 fold:bg-black'>
      <div className='pointer-events-none fixed inset-0 z-0'>
        <motion.img
          src={blurBg}
          alt=''
          className='absolute inset-0 hidden size-full object-cover fold:block'
          animate={{ scale: [1, 1.1, 1] }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <BackgroundBubbles count={40} />

        <img
          src={authElement}
          alt=''
          className='absolute bottom-[-30%] left-[40%] z-0 block w-[180%] max-w-none translate-x-[20%] translate-y-[20%] animate-spin-slow-120 object-contain opacity-60 fold:hidden'
        />
      </div>

      <div className='relative z-10 flex w-full flex-col items-center'>
        {authType === 'login' && (
          <div className='hidden w-[95%] items-center justify-between px-2 pb-3 fold:flex md:w-[90%] lg:hidden'>
            <a
              href={import.meta.env['VITE_LANDING_URL'] || 'https://dechoes.com'}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center font-sans text-18 font-medium leading-25 tracking-normal text-white underline decoration-solid hover:opacity-80'
            >
              <img src={rightArrow} alt='' className='mr-[14px] size-auto' />
              More about us
            </a>
            <img src={logoVarTwo} alt='D.ECHO' className='h-auto w-[100px]' />
          </div>
        )}

        {/* Desktop-only header — same left/right split as AuthCardFrame */}
        <div className='relative hidden w-[95%] items-center pb-1 md:w-[90%] lg:flex wide:w-[82vw] wide:gap-[2%] 4xl:max-w-[1537px] 4xl:gap-[2%]'>
          <div className='w-[50.76%] shrink-0 4xl:w-[47.9%]' />
          <div className='flex flex-1 flex-col items-center justify-center'>
            <div className='flex w-full items-center 4xl:max-w-[29rem]'>
              <a
                href={import.meta.env['VITE_LANDING_URL'] || 'https://dechoes.com'}
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center font-sans text-18 font-medium leading-25 tracking-normal text-white underline decoration-solid hover:opacity-80'
              >
                <img src={rightArrow} alt='' className='mr-[14px] size-auto' />
                More about us
              </a>
            </div>
          </div>
          <img
            src={logoVarTwo}
            alt='D.ECHO'
            className='absolute right-0 top-1/2 h-auto w-[120px] -translate-y-1/2'
          />
        </div>

        <div className='mt-4 flex w-full justify-center'>
          <AuthCardFrame>
            {authType === 'forgot' ? (
              <ForgotPassword onSwitch={onSwitch} />
            ) : authType === 'verify-pending' || authType === 'verify-complete' ? (
              children
            ) : (
              <>
                <AuthCarousel hasUtm={hasUtm} utmEcho={utmEcho ?? null} />

                <AuthFormPanel
                  authType={authType}
                  onSwitch={onSwitch}
                  isLoading={isLoading}
                  submitLabel={submitLabel}
                  dividerText={dividerText}
                  formId={formId}
                  hasUtm={hasUtm}
                  utmEcho={utmEcho ?? null}
                  isAuthenticated={isAuthenticated}
                  onChatNow={onChatNow}
                >
                  {children}
                </AuthFormPanel>
              </>
            )}
          </AuthCardFrame>
        </div>
      </div>
    </div>
  );
};
